/**
 * Coach focus-guidance — the discipline-imposer side of the Brain.
 *
 * Founder sync 2026-04-30 established the rule explicitly: the AI
 * must push back when a coach jumps themes too frequently. Quote:
 *   "The AI is not a yes-machine. When a coach asks for something
 *    that contradicts good practice for their context (jumping themes
 *    weekly... ), the AI should gently push back."
 *
 * This module computes the analytics — given a coach + their proposed
 * primary focus, return a structured report describing recent theme
 * variance and (when warranted) a brief prompt-generated message to
 * surface in the planner UI.
 *
 * Trigger heuristic (matches the Josh UI brief):
 *   - Pull last 4 sessions for the coach (sorted desc by date)
 *   - If > 3 distinct primary_phases across those 4 sessions → HIGH
 *   - If 3 distinct phases → MEDIUM
 *   - If proposed phase is the 5th distinct in 5 sessions → HIGH
 *   - Sub-theme overlap with last 4: if zero overlap, flag "fresh
 *     theme without consolidation"
 *
 * Read-only side-effect free analytics + a single conditional AI
 * call. Safe to invoke as a pre-flight from any planner surface.
 */

import { supabase } from './supabase';
import { callAIJson } from './ai';
import { inferPhaseOfPlay, type PhaseOfPlay } from './phaseOfPlay';
import { FOCUS_GUIDANCE_PROMPT } from './prompts/focusGuidance';

/** How many recent sessions to consider when computing variance. */
const LOOKBACK = 4;

export type VarianceLevel = 'low' | 'medium' | 'high';

export interface RecentSession {
  id: string;
  date: string;
  title: string;
  primaryPhase: PhaseOfPlay | null;
  subThemes: string[];
}

export interface CoachFocusReport {
  /** Last LOOKBACK sessions, newest first. May be < LOOKBACK for new coaches. */
  recentSessions: RecentSession[];
  /** Distinct primary_phase values across the lookback window. */
  distinctPhases: number;
  /** Distinct sub_theme values across the lookback window. */
  distinctSubThemes: number;
  varianceLevel: VarianceLevel;
  /** True when the proposed focus has no sub-theme overlap with any
   *  session in the lookback window — a fresh theme without
   *  consolidation. False if any overlap exists, OR if proposedPhase
   *  could not be inferred. */
  isFreshThemeWithoutConsolidation: boolean;
  /** True when at least one warning condition fired and the planner
   *  should surface a discipline-imposing message before generating. */
  shouldWarn: boolean;
  /** AI-drafted message in plain English, ≤2 sentences, surfaced in
   *  the UI banner. Null when shouldWarn is false. */
  message: string | null;
  /** Inferred phase of the coach's proposed focus, or null if the
   *  free-text didn't match any canonical phase. */
  proposedPhase: PhaseOfPlay | null;
  /** Sub-themes parsed from the proposed primary focus (best-effort). */
  proposedSubThemes: string[];
}

/** Lookup the coach's last LOOKBACK sessions and their tagging. */
async function loadRecentSessions(coachId: string): Promise<RecentSession[]> {
  const { data, error } = await supabase
    .from('sessions')
    .select('id, date, title, phase_coverage, session_drills:session_drills(drill:drills(primary_phase, sub_themes))')
    .eq('coach_id', coachId)
    .is('deleted_at', null)
    .order('date', { ascending: false })
    .limit(LOOKBACK);
  if (error || !data) return [];
  return data.map((s: any) => {
    const drills = Array.isArray(s.session_drills) ? s.session_drills : [];
    // Per-session primary_phase: pick the most common primary_phase of
    // the drills attached to it (the session itself doesn't store one
    // — primary_phase lives on each drill). Fall back to the highest
    // phase_coverage entry when no drill phase is set yet.
    const phaseCounts: Record<string, number> = {};
    const subThemeSet = new Set<string>();
    for (const sd of drills) {
      const d = sd?.drill;
      if (!d) continue;
      if (d.primary_phase) phaseCounts[d.primary_phase] = (phaseCounts[d.primary_phase] ?? 0) + 1;
      if (Array.isArray(d.sub_themes)) {
        for (const t of d.sub_themes) if (typeof t === 'string') subThemeSet.add(t);
      }
    }
    let primaryPhase: PhaseOfPlay | null = null;
    let topCount = 0;
    for (const [p, c] of Object.entries(phaseCounts)) {
      if (c > topCount) {
        topCount = c;
        primaryPhase = p as PhaseOfPlay;
      }
    }
    if (!primaryPhase && s.phase_coverage) {
      const cov = s.phase_coverage as Record<string, number>;
      let topPhase: string | null = null;
      let topPct = 0;
      for (const [p, pct] of Object.entries(cov)) {
        if (pct > topPct) {
          topPct = pct;
          topPhase = p;
        }
      }
      primaryPhase = (topPhase as PhaseOfPlay) ?? null;
    }
    return {
      id: s.id as string,
      date: s.date as string,
      title: (s.title as string) ?? '',
      primaryPhase,
      subThemes: Array.from(subThemeSet),
    };
  });
}

/** Best-effort parse of sub-themes from the coach's free-text focus.
 *  Looks for any canonical sub-theme keyword as a substring. Cheap +
 *  good-enough — the LLM can correct later. */
function parseProposedSubThemes(focus: string): string[] {
  const t = focus.toLowerCase();
  const out: string[] = [];
  const knownKeywords = [
    'pressing', 'counter-press', 'low-block', 'mid-block', 'high-block',
    'build-up', 'breaking-lines', 'breaking lines', 'width', 'depth',
    'crossing', 'finishing', 'switching', 'third-man', 'third man',
    'overload', 'set-piece', 'set piece', 'counter-attack', 'counter attack',
    'transition', 'turnover', 'first-touch', 'first touch', 'scanning',
    'decision', 'composure', 'goalkeeping', '1v1',
  ];
  for (const k of knownKeywords) {
    if (t.includes(k)) {
      // Normalise "third man" → "third-man" etc.
      out.push(k.replace(/\s+/g, '-'));
    }
  }
  return Array.from(new Set(out));
}

export interface EvaluateCoachFocusOptions {
  coachId: string;
  /** The coach's proposed primary focus for the new session. */
  primaryFocus: string;
  /** Optional secondary focus — broadens sub-theme overlap detection. */
  secondaryFocus?: string;
  /** Disable the AI message draft (analytics-only mode). Default false. */
  skipAiMessage?: boolean;
}

export async function evaluateCoachFocus(
  opts: EvaluateCoachFocusOptions,
): Promise<CoachFocusReport> {
  const recentSessions = await loadRecentSessions(opts.coachId);

  const proposedPhase = inferPhaseOfPlay(`${opts.primaryFocus} ${opts.secondaryFocus ?? ''}`);
  const proposedSubThemes = [
    ...parseProposedSubThemes(opts.primaryFocus),
    ...parseProposedSubThemes(opts.secondaryFocus ?? ''),
  ];
  const proposedSubThemeSet = new Set(proposedSubThemes);

  const phasesInWindow = new Set<string>();
  const subThemesInWindow = new Set<string>();
  for (const s of recentSessions) {
    if (s.primaryPhase) phasesInWindow.add(s.primaryPhase);
    for (const t of s.subThemes) subThemesInWindow.add(t);
  }
  // Adding the proposed phase to the variance count only if it's truly
  // distinct — emulates "what would the variance look like AFTER this
  // session lands."
  const phasesIfAdded = new Set(phasesInWindow);
  if (proposedPhase) phasesIfAdded.add(proposedPhase);

  const distinctPhases = phasesIfAdded.size;
  const distinctSubThemes = subThemesInWindow.size;

  let varianceLevel: VarianceLevel = 'low';
  if (recentSessions.length >= 3 && distinctPhases >= 4) varianceLevel = 'high';
  else if (recentSessions.length >= 3 && distinctPhases === 3) varianceLevel = 'medium';

  // Fresh-theme-without-consolidation: proposed sub-themes don't
  // overlap with anything in the last 4 sessions. Only meaningful
  // when we have at least 2 prior sessions to compare against AND
  // the coach actually proposed a recognisable sub-theme.
  let isFreshThemeWithoutConsolidation = false;
  if (recentSessions.length >= 2 && proposedSubThemes.length > 0) {
    const overlap = proposedSubThemes.some((t) => subThemesInWindow.has(t));
    isFreshThemeWithoutConsolidation = !overlap;
  }

  const shouldWarn =
    varianceLevel === 'high' ||
    (varianceLevel === 'medium' && isFreshThemeWithoutConsolidation);

  let message: string | null = null;
  if (shouldWarn && !opts.skipAiMessage) {
    message = await draftFocusMessage({
      recentSessions,
      proposedFocus: opts.primaryFocus,
      proposedPhase,
      varianceLevel,
      isFreshThemeWithoutConsolidation,
    });
  }

  return {
    recentSessions,
    distinctPhases,
    distinctSubThemes,
    varianceLevel,
    isFreshThemeWithoutConsolidation,
    shouldWarn,
    message,
    proposedPhase,
    proposedSubThemes: Array.from(proposedSubThemeSet),
  };
}

interface DraftMessageInput {
  recentSessions: RecentSession[];
  proposedFocus: string;
  proposedPhase: PhaseOfPlay | null;
  varianceLevel: VarianceLevel;
  isFreshThemeWithoutConsolidation: boolean;
}

async function draftFocusMessage(input: DraftMessageInput): Promise<string | null> {
  const summary = input.recentSessions.map((s) => ({
    date: s.date,
    title: s.title,
    primaryPhase: s.primaryPhase,
    subThemes: s.subThemes,
  }));
  const userContent = JSON.stringify({
    proposedFocus: input.proposedFocus,
    proposedPhase: input.proposedPhase,
    varianceLevel: input.varianceLevel,
    isFreshThemeWithoutConsolidation: input.isFreshThemeWithoutConsolidation,
    recentSessions: summary,
  });
  try {
    const result = await callAIJson<{ message: string }>({
      system: FOCUS_GUIDANCE_PROMPT,
      userContent,
      maxTokens: 200,
      surface: 'coach-focus-guidance',
    });
    if (!result?.message) return null;
    return result.message.trim().slice(0, 320);
  } catch {
    return null;
  }
}
